import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CalendarEvent } from '@fittrack/ui';
import apiClient from '../api/client';
import { useMember } from '../context/MemberContext';
import './Page.css';
import './SchedulePreviewPage.css';

interface MyClass {
  calendarInstanceId: number;
  calendarName: string;
  calendarColor: string;
  startDate: string;
  endDate: string;
  status: string;
}

type ScheduleTab = 'browse' | 'myclasses';

interface CalendarOption {
  calendarId: number;
  name: string;
  color?: string;
  calendarTypeId?: number;
}

type BookingState =
  | { status: 'idle' }
  | { status: 'loading'; event: CalendarEvent }
  | { status: 'success'; message: string }
  | { status: 'error'; message: string }
  | { status: 'no-sessions' };

function CalIcon({ typeId }: { typeId?: number }) {
  const p = {
    width: 26, height: 26, viewBox: '0 0 24 24',
    fill: 'none', stroke: '#fff', strokeWidth: 2,
    strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
  };
  if (typeId === 1)
    return <svg {...p}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>;
  if (typeId === 2)
    return <svg {...p}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>;
  if (typeId === 3)
    return <svg {...p}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>;
  return (
    <svg {...p}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function fmtDate(d: Date) {
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function fmtTime(d: Date) {
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function fmtDateShort(d: Date) {
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function groupByDay(events: CalendarEvent[]) {
  const map = new Map<string, CalendarEvent[]>();
  for (const e of events) {
    const key = (e.startTime as Date).toDateString();
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(e);
  }
  return Array.from(map.entries()).map(([key, evs]) => ({
    label: fmtDate(new Date(key)),
    events: evs,
  }));
}

export default function SchedulePreviewPage() {
  const { member } = useMember();
  const navigate = useNavigate();
  const siteId = member?.siteId ?? 0;

  const [tab, setTab] = useState<ScheduleTab>('browse');
  const [calendars, setCalendars] = useState<CalendarOption[]>([]);
  const [selectedCal, setSelectedCal] = useState<CalendarOption | undefined>(undefined);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [booking, setBooking] = useState<BookingState>({ status: 'idle' });
  const [enrolledInstanceIds, setEnrolledInstanceIds] = useState<Set<number>>(new Set());
  const [reloadToken, setReloadToken] = useState(0);
  const [sessionInfo, setSessionInfo] = useState<{ availableSessions: number; hasUnlimitedSessions: boolean } | null>(null);
  const [myClasses, setMyClasses] = useState<MyClass[]>([]);
  const [myClassesLoading, setMyClassesLoading] = useState(false);

  useEffect(() => {
    if (!siteId) return;
    apiClient
      .get(`/Schedule/calendars?siteId=${siteId}`)
      .then(res => {
        const data: CalendarOption[] = res.data?.data ?? res.data ?? [];
        setCalendars([...data].sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(err => console.error('Failed to load calendars', err));
  }, [siteId]);

  useEffect(() => {
    if (!selectedCal || !member?.personId) { setSessionInfo(null); return; }
    apiClient
      .get(`/Schedule/member-sessions?personId=${member.personId}&calendarId=${selectedCal.calendarId}`)
      .then(res => setSessionInfo(res.data?.data ?? res.data ?? null))
      .catch(() => setSessionInfo(null));
  }, [selectedCal, member?.personId]);

  useEffect(() => {
    if (!selectedCal) { setEvents([]); return; }
    setEventsLoading(true);
    const start = new Date();
    const end = new Date();
    end.setDate(end.getDate() + 60);
    const params = new URLSearchParams({
      siteId: siteId.toString(),
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      calendarIds: selectedCal.calendarId.toString(),
      ...(member?.personId ? { personId: member.personId.toString() } : {}),
    });
    apiClient
      .get(`/Schedule/events?${params}`)
      .then(res => {
        const data = (res.data?.data ?? res.data ?? []) as CalendarEvent[];
        setEvents(
          data.map(e => ({
            ...e,
            startTime: new Date(e.startTime as string),
            endTime: new Date(e.endTime as string),
          }))
        );
      })
      .catch(() => setEvents([]))
      .finally(() => setEventsLoading(false));
  }, [selectedCal, siteId, reloadToken]);

  useEffect(() => {
    if (tab !== 'myclasses' || !member?.personId) return;
    setMyClassesLoading(true);
    apiClient
      .get(`/PwaDashboard/myclasses/${member.personId}`)
      .then(res => setMyClasses(res.data?.data ?? []))
      .catch(() => setMyClasses([]))
      .finally(() => setMyClassesLoading(false));
  }, [tab, member?.personId, reloadToken]);

  const handleBook = useCallback(async (event: CalendarEvent) => {
    if (!member?.personId) return;
    setBooking({ status: 'loading', event });
    try {
      const res = await apiClient.post('/Schedule/book-member', {
        personId: member.personId,
        calendarInstanceId: event.calendarInstanceId,
      });
      const data = res.data as { success: boolean; message: string; noSessionsAvailable?: boolean };
      if (data.noSessionsAvailable) {
        setBooking({ status: 'no-sessions' });
      } else {
        // Mark this instance as enrolled and refresh the event list
        setEnrolledInstanceIds(prev => new Set(prev).add(event.calendarInstanceId));
        setReloadToken(t => t + 1);
        setBooking({ status: 'success', message: data.message });
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { noSessionsAvailable?: boolean; message?: string } } };
      if (axiosErr.response?.data?.noSessionsAvailable) {
        setBooking({ status: 'no-sessions' });
      } else {
        setBooking({
          status: 'error',
          message: axiosErr.response?.data?.message ?? 'Something went wrong. Please try again.',
        });
      }
    }
  }, [member?.personId]);

  const dismiss = useCallback(() => setBooking({ status: 'idle' }), []);

  if (!siteId) return null;

  const days = groupByDay(events);

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <header className="page-header">
        {tab === 'browse' && selectedCal ? (
          <div className="cal-back-bar">
            <button type="button" className="cal-back-btn" onClick={() => setSelectedCal(undefined)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div className="cal-header-info">
              <h1 className="page-header__title">{selectedCal.name}</h1>
              {sessionInfo && (
                <span className="cal-sessions-badge">
                  {sessionInfo.hasUnlimitedSessions
                    ? 'Unlimited access'
                    : `${sessionInfo.availableSessions} session${sessionInfo.availableSessions !== 1 ? 's' : ''} available`}
                </span>
              )}
            </div>
          </div>
        ) : (
          <h1 className="page-header__title">Schedule</h1>
        )}
        <div className="sched-tabs">
          <button
            type="button"
            className={`sched-tab${tab === 'browse' ? ' sched-tab--active' : ''}`}
            onClick={() => setTab('browse')}
          >
            Browse
          </button>
          <button
            type="button"
            className={`sched-tab${tab === 'myclasses' ? ' sched-tab--active' : ''}`}
            onClick={() => { setTab('myclasses'); setSelectedCal(undefined); }}
          >
            My Classes
          </button>
        </div>
      </header>

      {tab === 'myclasses' ? (
        <div className="page-content">
          {myClassesLoading && <div className="cal-spinner" />}
          {!myClassesLoading && myClasses.length === 0 && (
            <p className="cal-empty">No upcoming classes booked.</p>
          )}
          {!myClassesLoading && myClasses.map(cls => {
            const start = new Date(cls.startDate);
            const end   = new Date(cls.endDate);
            return (
              <div key={cls.calendarInstanceId} className="cal-event-card cal-event-card--enrolled">
                <div className="cal-event-card__accent" style={{ background: cls.calendarColor || 'var(--accent,#e94560)' }} />
                <div className="cal-event-card__body">
                  <p className="cal-event-card__time">{cls.calendarName}</p>
                  <p className="cal-event-card__spots">
                    {fmtDateShort(start)} · {fmtTime(start)} – {fmtTime(end)}
                  </p>
                  <p className="cal-event-card__spots cal-event-card__spots--enrolled">Enrolled</p>
                </div>
              </div>
            );
          })}
        </div>
      ) : selectedCal === undefined ? (
        <div className="page-content">
          <div className="cal-grid-wrap">
            {calendars.map(c => (
              <button
                key={c.calendarId}
                type="button"
                className="cal-tile"
                onClick={() => setSelectedCal(c)}
              >
                <span className="cal-tile__icon" style={{ background: c.color ?? 'rgba(255,255,255,0.12)' }}>
                  <CalIcon typeId={c.calendarTypeId} />
                </span>
                <span className="cal-tile__name">{c.name}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="page-content">
          {eventsLoading && <div className="cal-spinner" />}

          {!eventsLoading && events.length === 0 && (
            <p className="cal-empty">No upcoming sessions in the next 60 days.</p>
          )}

          {!eventsLoading && days.map(day => (
            <div key={day.label} className="cal-day-group">
              <p className="cal-day-label">{day.label}</p>
              {day.events.map(ev => {
                const spotsLeft = ev.maxAttendees - ev.currentAttendees;
                // Backend is source of truth; local set gives instant feedback before the reload
                const isEnrolled = ev.isEnrolled || enrolledInstanceIds.has(ev.calendarInstanceId);
                return (
                  <div key={ev.calendarInstanceId} className={`cal-event-card${isEnrolled ? ' cal-event-card--enrolled' : ''}`}>
                    <div className="cal-event-card__accent" style={{ background: selectedCal.color ?? 'var(--accent, #e94560)' }} />
                    <div className="cal-event-card__body">
                      <p className="cal-event-card__time">
                        {fmtTime(ev.startTime as Date)} – {fmtTime(ev.endTime as Date)}
                      </p>
                      <p className={`cal-event-card__spots${isEnrolled ? ' cal-event-card__spots--enrolled' : ev.isFull ? ' cal-event-card__spots--full' : ''}`}>
                        {isEnrolled
                          ? 'Enrolled!!'
                          : ev.isFull
                            ? 'Class full'
                            : `${spotsLeft} of ${ev.maxAttendees} spots available`}
                      </p>
                      <button
                        type="button"
                        className={`cal-event-card__btn${isEnrolled ? ' cal-event-card__btn--enrolled' : ev.isFull ? ' cal-event-card__btn--full' : ''}`}
                        disabled={isEnrolled || ev.isFull || booking.status === 'loading'}
                        onClick={() => handleBook(ev)}
                      >
                        {isEnrolled ? 'Enrolled' : ev.isFull ? 'Full' : 'Book Session'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {booking.status === 'loading' && (
        <div className="sp-overlay">
          <div className="sp-modal">
            <p className="sp-modal__message">Booking your session…</p>
          </div>
        </div>
      )}

      {booking.status === 'success' && (
        <div className="sp-overlay" onClick={dismiss}>
          <div className="sp-modal sp-modal--success" onClick={e => e.stopPropagation()}>
            <div className="sp-modal__icon">😊</div>
            <p className="sp-modal__headline">Booking Succeeded!</p>
            <p className="sp-modal__detail">{booking.message}</p>
            <button className="sp-modal__btn sp-modal__btn--primary" onClick={dismiss}>
              Let's go!
            </button>
          </div>
        </div>
      )}

      {booking.status === 'error' && (
        <div className="sp-overlay" onClick={dismiss}>
          <div className="sp-modal" onClick={e => e.stopPropagation()}>
            <p className="sp-modal__message sp-modal__message--error">{booking.message}</p>
            <button className="sp-modal__btn sp-modal__btn--secondary" onClick={dismiss}>OK</button>
          </div>
        </div>
      )}

      {booking.status === 'no-sessions' && (
        <div className="sp-overlay">
          <div className="sp-modal">
            <p className="sp-modal__message">
              You have no available sessions. You need to purchase a new package to book this class.
            </p>
            <button
              className="sp-modal__btn sp-modal__btn--primary"
              onClick={() => {
                dismiss();
                navigate('/enroll', { state: { calendarId: selectedCal?.calendarId } });
              }}
            >
              View Packages
            </button>
            <button className="sp-modal__btn sp-modal__btn--secondary" onClick={dismiss}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
